import { Component, ElementRef, Input, OnInit, ViewChild } from '@angular/core';
import { Store } from '@ngrx/store';
import { InceptionService, TranslationService } from '@proximus/ng-common';
import { Alert, AlertsService } from '@proximus/ng-common-components';
import { OnDestroyHookDirective } from 'src/app/shared/hooks';
import {
  AddMacIpAddress,
  DeleteMacIpAddress,
  GetMacIpAddress,
  UpdateMacIpAddress,
} from 'src/app/store/actions/internet-settings.actions';
import { ApplicationState, getMacIpAddressState } from 'src/app/store/reducers';

@Component({
  selector: 'mprd-fixed-ip',
  templateUrl: './fixed-ip.component.html',
})
export class FixedIpComponent extends OnDestroyHookDirective implements OnInit {
  @Input() circuitId: string;

  isMacAddressValid = false;
  fixedIpMacAddress: string;
  isNewMacAddress = true;
  pending = false;
  @ViewChild('newMacAddressInput') newMacAddressInput;

  constructor(
    private translationService: TranslationService,
    private alertService: AlertsService,
    private inceptionService: InceptionService,
    private elementRef: ElementRef,
    private store: Store<ApplicationState>,
  ) {
    super();
  }

  ngOnInit(): void {
    this.initInception();
    this.store.dispatch(new GetMacIpAddress(this.circuitId));

    this.addSubscription(
      this.store.select(getMacIpAddressState).subscribe((value) => {
        this.pending = value.pending;
        if (value.getSuccess) {
          this.fixedIpMacAddress = value.macAddress;
          this.validateMacAddress();
          this.isNewMacAddress =
            !value.macAddress || value.macAddress.length === 0;
        }
        if (value.addSuccess) {
          this.newMacAddressInput = false;
          this.isNewMacAddress = false;
          this.dispatchAlert(
            this.translationService.getTranslatedValueFor(
              'internet.settings.fix.ip.mac.address.add.success.title',
            ),
            this.translationService.getTranslatedValueFor(
              'internet.settings.fix.ip.mac.address.add.success.description',
            ),
            'success',
          );
        }
        if (value.updateSuccess) {
          this.isNewMacAddress = false;
          this.dispatchAlert(
            this.translationService.getTranslatedValueFor(
              'internet.settings.fix.ip.mac.address.update.success.title',
            ),
            this.translationService.getTranslatedValueFor(
              'internet.settings.fix.ip.mac.address.update.success.description',
            ),
            'success',
          );
        }
        if (value.deleteSuccess) {
          this.newMacAddressInput = true;
          this.fixedIpMacAddress = value.macAddress;
          this.isNewMacAddress =
            !value.macAddress || value.macAddress.length === 0;
          this.dispatchAlert(
            this.translationService.getTranslatedValueFor(
              'internet.settings.fix.ip.mac.address.delete.success.title',
            ),
            this.translationService.getTranslatedValueFor(
              'internet.settings.fix.ip.mac.address.delete.success.description',
            ),
            'success',
          );
        }
        if (value.addError) {
          this.dispatchAlert(
            this.translationService.getTranslatedValueFor(
              'internet.settings.fix.ip.mac.address.add.error.title',
            ),
            this.translationService.getTranslatedValueFor(
              'internet.settings.fix.ip.mac.address.add.error.description',
            ),
            'error',
          );
        }
        if (value.updateError) {
          this.dispatchAlert(
            this.translationService.getTranslatedValueFor(
              'internet.settings.fix.ip.mac.address.update.error.title',
            ),
            this.translationService.getTranslatedValueFor(
              'internet.settings.fix.ip.mac.address.update.error.description',
            ),
            'error',
          );
        }
        if (value.deleteError) {
          this.dispatchAlert(
            this.translationService.getTranslatedValueFor(
              'internet.settings.fix.ip.mac.address.delete.error.title',
            ),
            this.translationService.getTranslatedValueFor(
              'internet.settings.fix.ip.mac.address.delete.error.description',
            ),
            'error',
          );
        }
        if (value.getError) {
          this.fixedIpMacAddress = value.macAddress;
          this.dispatchAlert(
            this.translationService.getTranslatedValueFor(
              'internet.settings.fix.ip.mac.address.get.error.title',
            ),
            this.translationService.getTranslatedValueFor(
              'internet.settings.fix.ip.mac.address.get.error.description',
            ),
            'error',
          );
        }
      }),
    );
  }

  private initInception() {
    this.inceptionService.init();
    this.inceptionService.panelAccordion(this.elementRef);
    this.inceptionService.forms(this.elementRef);
  }

  validateMacAddress(): void {
    this.isMacAddressValid = this.isValidMACAddress();
  }

  isValidMACAddress(): boolean {
    // Regex to check valid
    // MAC_Address
    const regex = new RegExp(/^([0-9A-Fa-f]{2}[:-]){5}([0-9A-Fa-f]{2})$/);

    // if str
    // is empty return false
    if (this.fixedIpMacAddress == null) {
      return false;
    }

    // Return true if the str
    // matched the ReGex
    if (regex.test(this.fixedIpMacAddress) == true) {
      return true;
    } else {
      return false;
    }
  }

  isNewMacAddressEmpty(): boolean {
    return this.fixedIpMacAddress?.length === 0;
  }

  updateOrAddMacIpAddress() {
    if (this.isValidMACAddress()) {
      if (this.isNewMacAddress) {
        this.addMacIpAddress();
      } else {
        this.updateMacIpAddress();
      }
    }
  }

  updateMacIpAddress() {
    this.store.dispatch(
      new UpdateMacIpAddress({
        circuitId: this.circuitId,
        fixedIpMacAddress: this.fixedIpMacAddress,
      }),
    );
  }

  addMacIpAddress() {
    this.store.dispatch(
      new AddMacIpAddress({
        circuitId: this.circuitId,
        fixedIpMacAddress: this.fixedIpMacAddress,
      }),
    );
  }

  deleteMacIpAddress() {
    this.store.dispatch(new DeleteMacIpAddress(this.circuitId));
  }

  private dispatchAlert(
    title: string,
    description: string,
    type: 'error' | 'info' | 'success' | 'warning',
  ): void {
    let taggingData: any;

    if (type === 'error') {
      taggingData = {
        event: 'popup-impression',
        button: {
          zone: this.translationService.getTranslatedValueFor(
            'internet.settings.fix.ip.title',
          ),
          name: 'change error',
        },
        error: {
          type: 'backend',
          message: description,
        },
      };
    } else if (type === 'success') {
      taggingData = {
        event: 'popup-impression',
        button: {
          zone: this.translationService.getTranslatedValueFor(
            'internet.settings.fix.ip.title',
          ),
          name: 'change success',
        },
      };
    }
    const alert: Alert = {
      isClosable: true,
      alertKey: 'updateFixedIpAlert',
      title,
      description,
      type,
      taggingData,
    };
    this.alertService.dispatchAlert(alert);
  }
}
